import type { ReactElement } from 'react'
import { fromJS } from 'immutable'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useRef, useState } from 'react'
import cc from 'classcat'
import loadable from '@loadable/component'

import {
  getLocationStringForGA,
  sendProductSliderImpressions,
  trackProductClick,
} from '../../../../../utils/googleAnalytics'
import { loadProductsByIds } from '../../../../../store/actions'
import { useTranslate } from '../../../../../utils/translate'
import EmptyProductListItem from '../../../EmptyProductListItem'
import PluginPlaceholder from '../components/PluginPlaceholder/PluginPlaceholder'
import ProductListItem from '../../../ProductListItem'
import ProductListSwiper from '../../../../ProductListSwiper'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './ProductSliderSettings'))

export function ProductSliderPlugin({
  editorMode,
  onEdit,
  editorView,
  data = fromJS({}),
  config,
  onDataChange,
  onCancel,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const [isActive, setIsActive] = useState(false)
  const products = useSelector<State, ImmutableList>((state) =>
    state.getIn(['productData', data.get('id'), 'products'], fromJS([])),
  )

  const locationPathname = useLocation().pathname
  const sellingCountryId = useSelector<State, number>((state) => state.getIn(['shop', 'sellingCountryId'])) || null
  const ref = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const t = useTranslate('interface')

  const [error, setError] = useState(false)

  const pluginActiveClasses = cc(['product-slider', { 'dali-grid-element-highlighted': isActive }])

  const visibleProducts = (products.filter((product) => product?.get('isVisible')).toJS() as Core.Product[]).map(
    (product: Core.Product) => ({ ...product, isLoaded: true }),
  )
  const productList = { products: visibleProducts, totalNumberOfProducts: visibleProducts.length }

  if (error || (!editorView && !data.get('productIds').size)) {
    return null
  }

  // catch the error and handle it in the wrapper to prevent the enclosing jsx from being rendered
  // as if the initial load fails, we do not know how many slides to render and without switching slidegroups it is impossible to recover
  async function loadProducts() {
    try {
      await dispatch(
        loadProductsByIds(data.get('id'), Array.from(data.get('productIds')), sellingCountryId, {
          showErrorNotification: false,
        }),
      )
    } catch {
      setError(true)
    }
  }

  const renderProductItem = (product: Core.Product, index: number) => (
    <ProductListItem
      product={product as Frontend.Product}
      trackProductClick={trackProductClick([getLocationStringForGA(locationPathname), 'product-slider'])}
      productIndex={index}
      key={product.productId}
    />
  )

  return (
    <div className={pluginActiveClasses} ref={ref}>
      <SettingsLayer
        virtualRef={ref}
        side="top"
        align="center"
        sideOffset={-600}
        open={editorMode === 'edit'}
        onEscapeKeyDown={(event) =>
          event.target instanceof HTMLElement && event.target.tagName !== 'INPUT' && onCancel()
        }
        className="dali-settingslayer-productslider"
        onActiveStateChange={setIsActive}
      >
        {({ renderLayout }) => (
          <SettingsForm
            {...{ data, config, onDataChange, onCancel, onSave, renderLayout, selectedProducts: products }}
          />
        )}
      </SettingsLayer>
      {editorView && !data.get('productIds').size ? (
        <PluginPlaceholder iconClassName="plugin-placeholder-productslider" onAdd={() => onEdit()}>
          {t('components.productSliderComponent.selectProductsButton.label')}
        </PluginPlaceholder>
      ) : (
        <ProductListSwiper
          loadProducts={loadProducts}
          productList={visibleProducts.length > 0 ? productList : undefined}
          renderProductItem={renderProductItem}
          renderEmptyProductItem={(index) => <EmptyProductListItem key={'emptySlide_' + index} />}
          uniqueId={data.get('id')}
          breakpoints={['480px', '768px', '992px']}
          productSize={data.get('imageSize', 'S')}
          trackProductList={sendProductSliderImpressions(getLocationStringForGA(locationPathname))}
        />
      )}
    </div>
  )
}
